import { ModalService, useRTL } from '@capsicum/common-web';
import { ChevronLeftIcon, ChevronRightIcon } from '@capsicum/common-web/icons';
import { type MarketingBannerDetails, MarketingBannerThemes } from '@capsicum/features/feeds/shared';
import { useCallback, useId, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { useMarketingBannerTextColor } from '../../hooks/useMarketingBannerTextColor';

import {
	BannerImage,
	MARKETING_BANNER_GRADIENT_MAP,
	MARKETING_BANNER_WRAPPER_MAP,
	StyledText
} from './MarketingBanner.styled';
import { MarketingBannerModal } from './MarketingBannerModal';
import { MarketingBannerModalFooter } from './MarketingBannerModalFooter';

type MarketingBannerProps = Omit<MarketingBannerDetails, 'id'> & {
	forceActionContainerVisibility?: boolean;
	height?: number;
	indentAction?: boolean;
	testID?: string;
};

export const MarketingBanner = ({
	action,
	body,
	cta,
	forceActionContainerVisibility = false,
	header,
	height,
	image,
	indentAction: indentActionProp = false,
	subtitle,
	testID = 'marketing-banner',
	theme = MarketingBannerThemes.SOCIOS
}: MarketingBannerProps) => {
	const titleID = useId();
	const { isRTL } = useRTL();
	const isClickable = !!action;
	const indentAction = indentActionProp || !!image;
	const { textColor } = useMarketingBannerTextColor({ theme });
	const isActionContainerVisible = forceActionContainerVisibility || !!cta || !!action;
	const Wrapper = MARKETING_BANNER_WRAPPER_MAP[theme] ?? MARKETING_BANNER_WRAPPER_MAP[MarketingBannerThemes.SOCIOS];
	const isCustomBanner = theme === MarketingBannerThemes.CUSTOM;

	const onClick = useCallback(() => {
		if (!action) return;

		switch (action.type) {
			case 'modal': {
				ModalService.addModal({
					background:
						isCustomBanner && action.backgroundColor
							? action.backgroundColor
							: MARKETING_BANNER_GRADIENT_MAP[theme],
					closeIconColor: textColor,
					detached: false,
					fullHeight: true,
					renderComponent: () => (
						<MarketingBannerModal
							backgroundImage={action.image}
							body={action.body ?? ''}
							header={header}
							image={image}
							primaryAction={() => action.primary.url && (window.location.href = action.primary.url)}
							subtitle={subtitle}
							theme={theme}
						/>
					),
					renderFooter: ({ close }) =>
						!isCustomBanner ? (
							<MarketingBannerModalFooter
								primary={action.primary}
								secondary={action.secondary}
								theme={theme}
								onClose={close}
							/>
						) : (
							<></>
						)
				});
				break;
			}
			case 'link': {
				window.location.href = action.url;

				break;
			}
		}

		action.onTrigger?.();
	}, [image, theme, action, header, subtitle, textColor]);

	const banner = useMemo(
		() => (
			<Wrapper
				$backgroundImage={isCustomBanner ? image?.url : undefined}
				$clickable={isClickable}
				$height={height}
				aria-labelledby={titleID}
				data-testid={testID}
				role={isClickable ? 'button' : 'article'}
				onClick={onClick}
			>
				{!isCustomBanner ? (
					<>
						<S.ContentContainer>
							{image && (
								<S.ImageContainer>
									<BannerImage
										alt="marketing-banner-carousel-image"
										blurhash={image.blurHash}
										fill
										src={image.url}
									/>
								</S.ImageContainer>
							)}

							<S.DetailsContainer>
								<S.Header>
									{subtitle && (
										<S.SingleLineText
											$color={textColor}
											aria-level={2}
											role="heading"
											variant="text4"
										>
											{subtitle}
										</S.SingleLineText>
									)}

									<S.SingleLineText
										$color={textColor}
										aria-level={1}
										id={titleID}
										role="heading"
										variant="title5"
									>
										{header}
									</S.SingleLineText>
								</S.Header>

								<S.MultiLineText $color={textColor} $lines={subtitle ? 2 : 3} variant="text5">
									{body}
								</S.MultiLineText>
							</S.DetailsContainer>
						</S.ContentContainer>

						{isActionContainerVisible && (
							<S.ActionContainer $indent={indentAction} $isRTL={isRTL}>
								<S.SingleLineText $color={textColor} variant="text5">
									{cta}
								</S.SingleLineText>

								{action &&
									(isRTL ? (
										<S.StyledChevronLeftIcon $color={textColor} />
									) : (
										<S.StyledChevronRightIcon $color={textColor} />
									))}
							</S.ActionContainer>
						)}
					</>
				) : null}
			</Wrapper>
		),
		[
			theme,
			isClickable,
			height,
			image,
			textColor,
			titleID,
			subtitle,
			header,
			body,
			cta,
			action,
			isRTL,
			onClick,
			testID,
			indentAction
		]
	);

	return banner;
};

namespace S {
	export const ContentContainer = styled.div`
		flex: 1;
		display: flex;
		overflow: hidden;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const ImageContainer = styled.div`
		flex: 0 0 ${({ theme }) => theme.sizing.xxlarge};
	`;

	export const DetailsContainer = styled.div`
		display: flex;
		overflow: hidden;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const ActionContainer = styled.div<{ $indent: boolean; $isRTL: boolean }>`
		display: flex;
		flex-basis: 28px;
		align-items: center;
		justify-content: space-between;
		padding: ${({ theme }) => theme.spacings.xxxsmall} 0 0;

		${({ $indent, $isRTL, theme }) =>
			$indent &&
			($isRTL
				? css`
						margin-right: calc(${theme.sizing.xxlarge} + ${theme.spacings.xxsmall});
					`
				: css`
						margin-left: calc(${theme.sizing.xxlarge} + ${theme.spacings.xxsmall});
					`)}
	`;

	export const Header = styled.header`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const SingleLineText = styled(StyledText)`
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	`;

	export const MultiLineText = styled(StyledText)<{ $lines: number }>`
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: ${({ $lines }) => $lines};
	`;

	export const StyledChevronRightIcon = styled(ChevronRightIcon)<{ $color: string }>`
		color: ${({ $color }) => $color};
	`;

	export const StyledChevronLeftIcon = styled(ChevronLeftIcon)<{ $color: string }>`
		color: ${({ $color }) => $color};
	`;
}
